import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ConfirmationComponent, LoadingService, Page, ToggleComponent } from '@serpro/ngx-suite-rfb';
import { cloneDeep, isEmpty } from 'lodash';
import { Subscription } from 'rxjs';
import { IDynamicPanel, OptionalParams } from 'src/app/shared/dynamic-panel/panel-interface';
import { MenuFeatureActionsService } from 'src/app/shared/menu-feature-actions/menu-feature-actions.service';
import { TipoMenu } from 'src/app/shared/model/feature';
import { ScopedMessageService } from 'src/app/shared/services/scoped-message.service';
import { CampoAlteravel } from '../../models/campo-alteravel';
import { OrigemErroDto } from '../../models/origem-erro-dto';
import { AtributoPagamento, Pagamento, ValorDocumento } from '../../models/pagamento';
import { ParametrosPesquisaPagamento } from '../../models/parametros-pesquisa';
import { TipoNIRDOC } from '../../models/tipo-ni-rdoc';
import { TipoOrigemErro } from '../../models/tipo-origem-erro';
import { TipoRetificacao } from '../../models/tipo-retificacao';
import { PagamentoComprovanteService } from '../../services/pagamento-comprovante.service';
import { PagamentoService } from '../../services/pagamento.service';
import { ReceitaDto } from '../../models/receita-dto';
import { DocumentoArrecadacaoRetificacaoBase1TO } from '../../models/documento-arrecadacao-retificacao-base1-to';

@Component({
  templateUrl: './retificacao-pagamento.component.html',
  styleUrls: ['./retificacao-pagamento.component.css'],
  providers: [ScopedMessageService]
})
export class RetificacaoPagamentoComponent implements IDynamicPanel, OnInit, OnDestroy, AfterViewInit {

  @Input() parametrosPesquisa: ParametrosPesquisaPagamento;
  @Input() atendimento: boolean = false;
  @Input() pagamento: Pagamento;
  @Input() docArrecRetificaBase1: DocumentoArrecadacaoRetificacaoBase1TO;
  
  @Input() tipoRetificacao: TipoRetificacao;
  @Input() camposAlteraveis: { [key: string]: CampoAlteravel };

  @ViewChildren('detalhes') detalhesQuery: QueryList<ToggleComponent>;
  @ViewChild('confirmaEmissaoComprovante') emissaoComprovante: ConfirmationComponent;
  @ViewChild('origemErroBanco') inputOrigemBanco: ElementRef<HTMLInputElement>;
  @ViewChild('origemErroNaoBanco') inputOrigemNaoBanco: ElementRef<HTMLInputElement>;
  @ViewChild('mensagemAlerta') mensagemAlerta: ElementRef;

  public AtributoPagamento: typeof AtributoPagamento = AtributoPagamento;
  public TipoRetificacao: typeof TipoRetificacao = TipoRetificacao;
  public TipoNIRDOC: typeof TipoNIRDOC = TipoNIRDOC;
  public TipoOrigemErro: typeof TipoOrigemErro = TipoOrigemErro;

  public titulo: string;
  filtroColapsado = false;
  public formGroup: UntypedFormGroup;
  public qtdListaValoresAlterados: number;
  public totalReceitaDe: number = 0;
  public totalReceitaPara: number;
  public receitas: ReceitaDto[] = [];
  public listaReceitas: any = [];
  public valorSelecionado: number = null

  public cnpjName: string = "cnpj";
  public codigoReceita: string = "codigoReceita0";
  public valorReceita: string = "valorReceita0";
  public receitaName: string = 'receita';

  public scopeId: string;
  public labelCombinada: string;
  public initError: Error;
  public featureEventSub: Subscription;
  public pageSize: number = 10;
  public currentPage: Page = {
    number: 1,
    first: 0,
    last: this.pageSize
  };
  public retificacaoRealizada: boolean = false;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private pagamentoService: PagamentoService,
    private messageService: ScopedMessageService,
    private menuFeatureActionsService: MenuFeatureActionsService,
    private pagamentoComprovanteService: PagamentoComprovanteService,
    private loadingService: LoadingService
  ) { }

  init(ni: string, params?: OptionalParams): void {
    this.parametrosPesquisa = params.parametrosPesquisa;
    this.atendimento = params.atendimento;
    this.pagamento = params.pagamento;
    this.tipoRetificacao = params.tipoRetificacao;
    this.camposAlteraveis = params.camposAlteraveis;
    this.docArrecRetificaBase1 = params.docArrecRetificaBase1;

    this.obterReceitas();

    this.featureEventSub = this.menuFeatureActionsService.featureActionEvent$
      .subscribe(featureEvent => {
        // se a chave de identificação da feature for a mesma
        if (featureEvent.active == false && featureEvent.featureAction.id == 'ConsultarPagamentos' && featureEvent.featureAction.tipoMenu == TipoMenu.ATENDIMENTO) {
          this.fecharRetificacao();
          this.featureEventSub.unsubscribe();
        }
      });
  }

  formatarItem(item: any): string {
    if (item && item.codigo && item.descricao) {
      return `${item.codigo} - ${item.descricao}`;
    }
    return '';
  }


  ngOnInit(): void {
    const numeroDocumento = isEmpty(this.pagamento.pgtoNrIdAr)
      ? '[NÚMERO NÃO DISPONÍVEL]'
      : this.pagamento.pgtoNrIdAr;

    this.titulo = `Pagamento - Retificação ${this.tipoRetificacao} -  No. do Documento - ${numeroDocumento}`;

    this.scopeId = this.messageService.scopeId;

    this.formGroup = this.formBuilder.group({
      ni: [''],
      tipoNi: [''],
      periodoApuracao: [''],
      dataVencimento: [''],
      processo: [''],
      referencia: [''],
      idCEF: [''],
      vrba: [''],
      vrbaPercentual: [''],
      codigoBanco: [''],
      codigoAgencia: [''],
      dataArrecadacao: [''],
      origemErro: [''],
      processoAlteracao: [''],
    });

    // Adiciona campos múltiplos [cnpj, recCd, recValor] ao {formGroup}
    for (let i = 1; i < 4; i++) {
      this.formGroup.addControl(`${this.cnpjName}${i}`, this.formBuilder.control(''));
      this.formGroup.addControl(`${this.codigoReceita}${i}`, this.formBuilder.control(''));
      this.formGroup.addControl(`${this.valorReceita}${i}`, this.formBuilder.control(''));
    }

    this.ajustarCamposAlteraveis();


  }

  private get receita(): ReceitaDto {
    return this.formGroup.get(this.receitaName).value;
  }

  ngAfterViewInit(): void {
    if (this.docArrecRetificaBase1.origemErro === TipoOrigemErro.BANCO) {
      this.inputOrigemBanco.nativeElement.checked = true;
    }

    else if (this.docArrecRetificaBase1.origemErro === TipoOrigemErro.NAO_BANCO) {
      this.inputOrigemNaoBanco.nativeElement.checked = true;
    }
  }

  private scrollParaMensagem(): void {
    document.getElementById("mensagemAlerta")?.scrollIntoView({ behavior: 'smooth' });
  }

  private ajustarCamposAlteraveis(): void {
    Object.values(this.camposAlteraveis).forEach(campo => {
      if (!campo.alteravel && this.formGroup.controls[campo.nomeCampo]) {
        this.formGroup.controls[campo.nomeCampo].disable();
      }

    })

    /*
    let itemCount = 0;

    if (this.pagamento.valoresDocumento != null) {
      this.qtdListaValoresAlterados = this.pagamento.valoresDocumento.length;
      this.pagamento.valoresDocumento.forEach(
        (valorReceita) => {
          this.totalReceitaDe += valorReceita.valor;
          if (itemCount > 0 || this.camposAlteraveis[AtributoPagamento.valoresReceita]?.alteravel == false) {
            this.formGroup.controls[this.codigoReceita + itemCount].disable();
          }
          if (this.camposAlteraveis[AtributoPagamento.valoresReceita]?.alteravel == false) {
            this.formGroup.controls[this.valorReceita + itemCount].disable();
          }
          itemCount++;
        }
      );
    }

    if (this.tipoRetificacao == TipoRetificacao.SIMPLIFICADA && this.camposAlteraveis[AtributoPagamento.ni]?.alteravel) {
      this.formGroup.controls[`${this.cnpjName}1`].disable();
      this.formGroup.controls[`${this.cnpjName}2`].enable();
      this.formGroup.controls[`${this.cnpjName}3`].enable();
    } else {
      this.formGroup.controls[`${this.cnpjName}1`].disable();
      this.formGroup.controls[`${this.cnpjName}2`].disable();
      this.formGroup.controls[`${this.cnpjName}3`].disable();
    }
      */
  }

  public carregaReceita() {
    const pagamentoAlterado: Pagamento = cloneDeep(this.pagamento);

    pagamentoAlterado.receita01.codigo = this.formGroup.controls['codigoReceita01'].value;
    this.pagamentoService.calcularValores(pagamentoAlterado).subscribe(
      listaValores => {
        this.qtdListaValoresAlterados = listaValores.length;

        for (let cont = 0; cont < listaValores.length; cont++) {
          this.formGroup.controls[this.codigoReceita + cont].setValue(listaValores[cont].recCd);
          this.formGroup.controls[this.valorReceita + cont].setValue(listaValores[cont].valor);
        }

        for (let cont = listaValores.length; cont < 3; cont++) {
          this.formGroup.controls[this.codigoReceita + cont].setValue('');
          this.formGroup.controls[this.valorReceita + cont].setValue('');
        }

        this.somaReceita();
      },
      erro => {
        this.messageService.showErrorException("Ocorreu um erro ao alterar o código de receita", erro);
      }
    );
  }

  public somaReceita(): void {
    this.totalReceitaPara = 0;

    this.totalReceitaPara += this.formGroup.controls['valorReceita01'].value &&
      Number(this.formGroup.controls['valorReceita01'].value != ''
        ? Number(this.formGroup.controls['valorReceita01'].value)
        : 0);

    this.totalReceitaPara += this.formGroup.controls['valorReceita02'].value &&
      Number(this.formGroup.controls['valorReceita02'].value != ''
        ? Number(this.formGroup.controls['valorReceita02'].value)
        : 0);

    this.totalReceitaPara += this.formGroup.controls['valorReceita03'].value &&
      Number(this.formGroup.controls['valorReceita03'].value != ''
        ? Number(this.formGroup.controls['valorReceita03'].value)
        : 0);
  }

  public async retificar() {
    const tipoRetificacao = this.tipoRetificacao == TipoRetificacao.COMPLETA ? "retificaDocumento" : "retificaDocumentoSimplificada"; 
    this.pagamentoService[tipoRetificacao](this.docArrecRetificaBase1).subscribe(
    );
  //  const pagamentoAlterado = this.gerarPagamentoAlterado();
    this.loadingService.show();
    await (async function (ms: number): Promise<void> {
      return new Promise(resolve => setTimeout(resolve, ms));
    })(500);
    this.loadingService.hide();
    this.messageService.showInfo('Retificação realizada com sucesso');
    this.retificacaoRealizada = true;
    this.scrollParaMensagem();
    this.habilitarComprovante();
    //  this.formGroup.reset();
    //this.emissaoComprovante.show();
    // this.pagamentoService.realizarRetificacao(pagamentoAlterado, this.tipoRetificacao).subscribe(
    //   () => {
    //     this.pagamento = pagamentoAlterado;
    //     this.pagamentoService.atualizarListagemPagamento(this.parametrosPesquisa);
    //     this.loadingService.hide();
    //     this.formGroup.reset();
    //     this.emissaoComprovante.show();
    //   },
    //   erro => {
    //     this.loadingService.hide();
    //     this.messageService.showErrorException("Ocorreu um erro ao solicitar a retificação", erro);
    //   }
    // )
  }

  get retificacaoCompleta(): boolean {
    return this.tipoRetificacao == this.TipoRetificacao.COMPLETA
  }   

  get retificacaoSimplificada(): boolean {
    return this.tipoRetificacao == this.TipoRetificacao.SIMPLIFICADA
  }

  get niEhCNPJ(): boolean {
    return this.pagamento.tipoNiRdoc == this.TipoNIRDOC.CNPJ;
  }

  get niEhCPF(): boolean {
    return this.pagamento.tipoNiRdoc == this.TipoNIRDOC.CPF;
  }

  private getFormValue(campo: string | AtributoPagamento): string | number | Date | null {
    const control = this.formGroup.get(campo);

    if (control == null || control.value == null || control.value == '') {
      return null;
    }

    if (typeof control.value === "string" && control.value.trim() == '') {
      return null;
    }

    return control.value;
  }

  private obterReceitas(): void {
    this.pagamentoService.obterReceitas().subscribe(
      response => {
        this.listaReceitas = response.map(item => ({...item, descricaoCompleta: item.codigo + ' - ' + item.descricao}))
      }
    )
  }
  

  private parseTipoNiFormValue(): TipoNIRDOC {
    const newNi = (this.getFormValue(AtributoPagamento.ni) as string)?.replace(/\D/g, "");

    if (newNi == null) {
      return null;
    }

    if (newNi.length == 11) {
      return TipoNIRDOC.CPF;
    }
    else if (newNi.length == 14) {
      return TipoNIRDOC.CNPJ;
    }

    return null;
  }

  private parseNiFormValue(): string {
    const newNi = (this.getFormValue(AtributoPagamento.ni) as string)?.replace(/\D/g, "");

    if (!this.pagamento.ni || this.pagamento.ni.length != 14 || this.tipoRetificacao != TipoRetificacao.SIMPLIFICADA) {
      return newNi;
    }

    const original = [
      this.pagamento.ni.substring(0, 8),
      this.pagamento.ni.substring(8, 12),
      this.pagamento.ni.substring(12, 14),
    ];

    const novo = [
      (this.getFormValue("cnpj0") as string)?.padStart(8, '0'),
      (this.getFormValue("cnpj1") as string)?.padStart(4, '0'),
      (this.getFormValue("cnpj2") as string)?.padStart(2, '0'),
    ];

    let newCnpj = "";

    for (let i = 0; i < original.length; i++) {
      newCnpj += novo[i] ?? original[i];
    }

    return newCnpj;
  }

  private parseValoresReceitasFormValue(): ValorDocumento[] {
    if (this.getFormValue(`${this.codigoReceita}0`) == null) {
      return null;
    }

    const valoresReceitas: ValorDocumento[] = [];
      
    for (let i = 0; i < 3; i++) {

      const codigoReceita = this.getFormValue(`${this.codigoReceita}${i}`) ? this.getFormValue(`${this.codigoReceita}${i}`) as number : this.docArrecRetificaBase1[`codigoReceita0${i+ 1}`];
      const valor = this.getFormValue(`${this.valorReceita}${i}`) as number;

      if (valor != null) {
        const receita = {
          codigo: codigoReceita,
          tipo: (i + 1).toString(),
          dataExtincao: null,
          descricao: null,
          nomeEditado: null,
        };

        // valoresReceitas[i] = { receita: receita, valor: valor };
      }
    }

    if (valoresReceitas.length === 0) {
      return null;
    }

    return valoresReceitas;
  }

  // TODO VIA-3571522: Revisar esses valores
  private parseOrigemBancoFormValue(): OrigemErroDto {
    if (this.inputOrigemBanco.nativeElement.checked) {
      return { codigo: 1, descricao: TipoOrigemErro.BANCO };
    }
    else if (this.inputOrigemNaoBanco.nativeElement.checked) {
      return { codigo: 2, descricao: TipoOrigemErro.NAO_BANCO };
    }
    else {
      return null;
    }
  }

  private parseFormValue(): Pagamento {
    const pagamentoAlterado = new Pagamento();

    pagamentoAlterado.ni = this.parseNiFormValue();
    pagamentoAlterado.tipoNiRdoc = this.parseTipoNiFormValue();
    // pagamentoAlterado.valoresReceitas = this.parseValoresReceitasFormValue();
    pagamentoAlterado.origemErro = this.parseOrigemBancoFormValue();

    for (const atributo of Object.values(AtributoPagamento)) {
      const value = this.getFormValue(atributo);

      if (pagamentoAlterado[atributo] != null || value == null) {
        continue;
      }

      pagamentoAlterado[atributo] = value;
    }

    return pagamentoAlterado;
  }

  private gerarPagamentoAlterado(): Pagamento {
    const pagamentoAlterado = this.parseFormValue();

    for (const key in this.pagamento) {
      pagamentoAlterado[key] = pagamentoAlterado[key] ?? this.pagamento[key];
    }

    return pagamentoAlterado
  }

  public ngOnDestroy(): void {
    if (this.featureEventSub) {
      this.featureEventSub.unsubscribe();
    }
  }

  private fecharRetificacao(): void {
    this.menuFeatureActionsService.deactivate({
      id: `pagamento_${this.pagamento.pgtoNr}`,
      title: `Pagamento - Retificação ${this.tipoRetificacao}`,
      class: RetificacaoPagamentoComponent,
      visible: true,
      rolesAllowed: [],
      tipoMenu: TipoMenu.ATENDIMENTO,
    });
  }

  public colapsar(valor: boolean): void {
    this.filtroColapsado = valor;
  }

  public formatarValor(campo: AtributoPagamento, decimais: number = 1) {
    const valor = this.getFormValue(campo) as string;

    if (valor) {
      const novoValor = Number(valor.replace('/\\./g', '')) / Math.pow(10, decimais);
      this.formGroup.controls[campo].setValue(novoValor.toString());
    }
  }

  public onEmitirComprovante(): void {
    this.loadingService.show();

    this.pagamentoComprovanteService.obterComprovanteRetificacao(this.pagamento).subscribe(
      response => {
        this.loadingService.hide();
        this.pagamentoComprovanteService.visualizarComprovante(response, 'Comprovante de Retificação');
      },
      error => {
        this.loadingService.hide();
        this.messageService.showErrorException('Ocorreu um erro ao emitir o comprovante de retificação', error);
      }
    );
  }

  private habilitarComprovante(): void {
    Object.values(this.formGroup.controls).forEach(control => {
      control.disable();
    });
  }
}
